import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Lock, Clock, CheckCircle2, ArrowLeft, UserPlus, Home } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register, registrationStatus, clearRegistrationStatus } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    role: 'resident',
    unit_number: '',
    admin_code: '',
    landlord_id: '',
    monthly_rent: ''
  });
  const [loading, setLoading] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [residents, setResidents] = useState([]);

  // Reset any stale registration status from a previous submission whenever
  // the user navigates to this page fresh (e.g., after "Try Login" or "Back to Home")
  useEffect(() => {
    setPendingStatus(null);
    if (clearRegistrationStatus) {
      clearRegistrationStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch active residents for tenant registration
  useEffect(() => {
    if (formData.role === 'tenant') {
      fetchResidents();
    }
  }, [formData.role]);

  const fetchResidents = async () => {
    try {
      const response = await axios.get(`${API}/users/residents-list`);
      setResidents(response.data);
    } catch (error) {
      // If endpoint doesn't exist, ignore
      console.log('Could not fetch residents list');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Frontend validation for tenant fields
    if (formData.role === 'tenant') {
      if (!formData.landlord_id || formData.landlord_id.trim() === '') {
        toast.error('Landlord ID is required for tenant registration');
        return;
      }
      if (!formData.unit_number || formData.unit_number.trim() === '') {
        toast.error('Unit Number is required for tenant registration');
        return;
      }
      if (!formData.monthly_rent || formData.monthly_rent <= 0) {
        toast.error('Monthly Rent is required for tenant registration');
        return;
      }
    }
    
    setLoading(true);
    try {
      // Prepare form data with proper type conversions
      const submitData = {
        ...formData,
        monthly_rent: formData.monthly_rent ? parseFloat(formData.monthly_rent) : null
      };
      
      const result = await register(submitData);
      
      if (result.status === 'active') {
        toast.success('Registration successful!');
        navigate(`/${result.user.role}`);
      } else {
        // Show waiting screen
        setPendingStatus(result.status);
        toast.success('Registration submitted! Waiting for approval.');
      }
    } catch (error) {
      // Handle error properly - detail might be string or object
      let errorMessage = 'Registration failed';
      const detail = error.response?.data?.detail;
      
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        // Pydantic validation errors come as array
        errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (detail && typeof detail === 'object') {
        errorMessage = detail.msg || detail.message || JSON.stringify(detail);
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Reset form for new registration
  const resetFormForNewRegistration = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      password: '',
      role: 'resident',
      unit_number: '',
      admin_code: '',
      landlord_id: '',
      monthly_rent: ''
    });
    setPendingStatus(null);
    // Also clear the context's registration status
    if (clearRegistrationStatus) {
      clearRegistrationStatus();
    }
  };

  // If user is waiting for approval
  if (pendingStatus || registrationStatus) {
    const status = pendingStatus || registrationStatus?.status;
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl">
          <CardHeader className="text-center">
            <div className="mx-auto h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-slate-900">Registration Submitted!</CardTitle>
            <CardDescription className="text-base">
              {status === 'pending_resident' 
                ? 'Your request is pending approval from your landlord (resident).'
                : status === 'pending_admin'
                ? 'Your request is pending final approval from admin.'
                : 'Your request is pending admin approval.'
              }
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Alert className="border-amber-200 bg-amber-50">
              <Clock className="h-5 w-5 text-amber-600" />
              <AlertTitle className="text-amber-800">Status: {status?.replace(/_/g, ' ').toUpperCase()}</AlertTitle>
              <AlertDescription className="text-amber-700">
                {status === 'pending_resident' 
                  ? 'Please wait for your landlord to review and approve your registration. Once approved, it will be forwarded to admin for final activation.'
                  : 'Please wait for the administrator to review and approve your registration. You will be able to login once approved.'
                }
              </AlertDescription>
            </Alert>
            
            <div className="text-center space-y-3">
              <p className="text-sm text-slate-600">
                You can try logging in after receiving approval, or register another user.
              </p>
              <div className="flex flex-col gap-2">
                <Button 
                  onClick={resetFormForNewRegistration}
                  className="w-full bg-secondary hover:bg-secondary/90"
                  data-testid="register-another-btn"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Register Another User
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/login')}
                  className="w-full"
                >
                  Try Login
                </Button>
                <Button 
                  variant="ghost" 
                  onClick={() => {
                    setPendingStatus(null);
                    navigate('/home');
                  }}
                  className="w-full"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Home
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 md:p-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <button
            onClick={() => navigate('/home')}
            className="flex items-center gap-3 hover:opacity-80 transition-opacity"
            data-testid="home-logo-btn"
          >
            <div className="h-12 w-12 bg-secondary rounded-xl flex items-center justify-center">
              <Lock className="h-6 w-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-primary" style={{ fontFamily: 'Manrope' }}>FortiHome</span>
          </button>
        </div>

        <Card className="shadow-xl border-slate-200 bg-white">
          <CardHeader className="space-y-4">
            <div className="flex justify-center">
              <div className="h-16 w-16 bg-secondary rounded-2xl flex items-center justify-center">
                <UserPlus className="h-8 w-8 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl md:text-3xl font-bold text-center text-slate-900" style={{ fontFamily: 'Manrope' }}>
              Create Account
            </CardTitle>
            <CardDescription className="text-center text-base">Join FortiHome community</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="role">I am a</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                  <SelectTrigger className="h-11" data-testid="register-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="resident">Resident (Flat Owner)</SelectItem>
                    <SelectItem value="tenant">Tenant (Renter)</SelectItem>
                    <SelectItem value="security">Security Professional</SelectItem>
                    <SelectItem value="admin">Administrator</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Role-specific info banner */}
              {formData.role !== 'admin' && (
                <Alert className="border-blue-200 bg-blue-50">
                  <Clock className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-700 text-sm">
                    {formData.role === 'tenant' 
                      ? 'Your registration requires approval from your landlord (resident) and then admin.'
                      : 'Your registration requires admin approval before you can login.'
                    }
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="register-name-input"
                  className="h-11"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="register-email-input"
                  className="h-11"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  placeholder="+91 98765 43210"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  data-testid="register-phone-input"
                  className="h-11"
                />
              </div>

              {/* Resident-specific fields */}
              {formData.role === 'resident' && (
                <div className="space-y-2">
                  <Label htmlFor="unit_number">Unit Number</Label>
                  <Input
                    id="unit_number"
                    placeholder="A-101"
                    value={formData.unit_number}
                    onChange={(e) => setFormData({ ...formData, unit_number: e.target.value })}
                    required
                    data-testid="register-unit-input"
                    className="h-11"
                  />
                </div>
              )}

              {/* Tenant-specific fields */}
              {formData.role === 'tenant' && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="landlord_id">Resident Code <span className="text-red-500">*</span></Label>
                    <Input
                      id="landlord_id"
                      placeholder="e.g., AB12CD34 (8 characters)"
                      value={formData.landlord_id}
                      onChange={(e) => setFormData({ ...formData, landlord_id: e.target.value.toUpperCase() })}
                      required
                      maxLength={8}
                      data-testid="register-landlord-input"
                      className={`h-11 font-mono uppercase ${!formData.landlord_id && 'border-red-300'}`}
                    />
                    <p className="text-xs text-slate-500">Ask your landlord for their unique 8-character Resident Code</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="unit_number">Unit/Flat Number <span className="text-red-500">*</span></Label>
                    <Input
                      id="unit_number"
                      placeholder="A-101 (required)"
                      value={formData.unit_number}
                      onChange={(e) => setFormData({ ...formData, unit_number: e.target.value })}
                      required
                      data-testid="register-tenant-unit-input"
                      className={`h-11 ${!formData.unit_number && 'border-red-300'}`}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="monthly_rent">Monthly Rent (₹) <span className="text-red-500">*</span></Label>
                    <Input
                      id="monthly_rent"
                      type="number"
                      placeholder="15000 (required)"
                      value={formData.monthly_rent}
                      onChange={(e) => setFormData({ ...formData, monthly_rent: e.target.value })}
                      required
                      min="1"
                      data-testid="register-rent-input"
                      className={`h-11 ${!formData.monthly_rent && 'border-red-300'}`}
                    />
                  </div>
                </>
              )}

              {/* Admin-specific fields */}
              {formData.role === 'admin' && (
                <div className="space-y-2">
                  <Label htmlFor="admin_code">Admin Code <span className="text-red-500">*</span></Label>
                  <Input
                    id="admin_code"
                    type="password"
                    placeholder="Enter confidential admin code"
                    value={formData.admin_code}
                    onChange={(e) => setFormData({ ...formData, admin_code: e.target.value })}
                    required
                    data-testid="register-admin-code-input"
                    className="h-11"
                  />
                  <p className="text-xs text-slate-500">Contact existing admin to get the confidential code</p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Create a strong password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  minLength={6}
                  data-testid="register-password-input"
                  className="h-11"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full h-11 bg-secondary hover:bg-secondary/90" 
                disabled={loading} 
                data-testid="register-submit-btn"
              >
                {loading ? 'Submitting...' : formData.role === 'admin' ? 'Create Admin Account' : 'Submit Registration'}
              </Button>
            </form>
            
            <div className="mt-6 text-center">
              <p className="text-slate-600">
                Already have an account?{' '}
                <Link to="/login" className="text-secondary font-semibold hover:underline" data-testid="register-login-link">
                  Sign in
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RegisterPage;
